import { createMemo, createSignal, createUniqueId, Show, lazy } from "solid-js"
import { createQuery } from "@tanstack/solid-query"
import { Icon } from "@opencode-ai/ui/icon"
import { IconButtonV2 } from "@opencode-ai/ui/v2/icon-button-v2"
import { TooltipV2 } from "@opencode-ai/ui/v2/tooltip-v2"
import { SessionFilePanelV2, SessionFilePanelV2Empty } from "@opencode-ai/session-ui/v2/session-file-panel-v2"
import { SessionReviewV2Sidebar } from "@opencode-ai/session-ui/v2/session-review-v2"
import FileTreeV2, { type Kind } from "@/components/file-tree-v2"
import { useFile } from "@/context/file"
import { useLanguage } from "@/context/language"
import { useLayout } from "@/context/layout"
import { useServer } from "@/context/server"
import { useSDK } from "@/context/sdk"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import { displayName } from "@/pages/layout/helpers"
import { useSessionLayout } from "@/pages/session/session-layout"
import { SessionFileView } from "@/pages/session/file-tabs"
import { applyFileListKeyDown, SessionFileListV2 } from "@/pages/session/v2/session-file-list-v2"
import { pathKey } from "@/utils/path-key"
import { downloadFile } from "@/utils/file-download"
import { uploadFile } from "@/utils/file-upload"
import { showToast } from "@/utils/toast"

const emptyFiles: string[] = []

const DialogSelectDirectoryV2 = lazy(() =>
  import("@/components/dialog-select-directory-v2").then((m) => ({ default: m.DialogSelectDirectoryV2 })),
)

export type SessionFileBrowserState = {
  sidebarOpened: () => boolean
  sidebarWidth: () => number
  sidebarTransition: () => boolean
  resizeSidebar: (width: number) => void
  toggleSidebar: () => void
}

export function SessionFileBrowserTab(props: {
  tab: string
  placeholder: boolean
  active?: string
  kinds: ReadonlyMap<string, Kind>
  state: SessionFileBrowserState
  onSelect: (path: string) => void
  onSelectPermanent: (path: string) => void
  filterRef?: (element: HTMLInputElement) => void
}) {
  const file = useFile()
  const language = useLanguage()
  const layout = useLayout()
  const sdk = useSDK()
  const server = useServer()
  const dialog = useDialog()
  const { workspaceKey } = useSessionLayout()
  const resultsID = `session-file-browser-results-${createUniqueId()}`
  const [filter, setFilter] = createSignal("")
  const [explicitHighlight, setExplicitHighlight] = createSignal<string>()
  const sidebarOpened = () => props.placeholder || props.state.sidebarOpened()
  const query = createMemo(() => filter().trim())
  const search = createQuery(() => {
    const value = query()
    return {
      queryKey: ["session-open-file", workspaceKey(), value] as const,
      enabled: value.length > 0,
      queryFn: ({ signal }) => file.searchFiles(value, { limit: 200, signal }),
    }
  })
  const files = createMemo(() => {
    if (!query() || search.isPending) return emptyFiles
    return [...new Set(search.data ?? emptyFiles)]
  })
  const highlighted = createMemo(() => {
    const values = files()
    if (values.length === 0) return undefined
    const explicit = explicitHighlight()
    if (explicit && values.includes(explicit)) return explicit
    return values[0]
  })
  const loading = createMemo(() => query().length > 0 && search.isPending)
  const project = createMemo(() => {
    const directory = pathKey(sdk().directory)
    return layout.projects
      .list()
      .find(
        (item) =>
          pathKey(item.worktree) === directory || item.sandboxes?.some((sandbox) => pathKey(sandbox) === directory),
      )
  })
  const title = createMemo(() => displayName(project() ?? { worktree: sdk().directory }))
  const optionID = (path: string) => `${resultsID}-option-${files().indexOf(path)}`

  const onFilterKeyDown = (event: KeyboardEvent & { currentTarget: HTMLInputElement }) => {
    if (event.key === "Escape" && query()) {
      event.preventDefault()
      setFilter("")
      return
    }
    if (!query()) return
    applyFileListKeyDown(event, files(), highlighted(), {
      onHighlight: setExplicitHighlight,
      onSelect: props.onSelectPermanent,
    })
  }

  const [uploading, setUploading] = createSignal(false)

  const handleDownload = async () => {
    const filePath = props.active
    if (!filePath) return
    const state = file.get(filePath)
    let content = state?.content
    if (!content) {
      await file.load(filePath)
      content = file.get(filePath)?.content
    }
    if (!content) {
      showToast({ variant: "error", title: "下载失败", description: "无法读取文件内容" })
      return
    }
    downloadFile(filePath, content)
  }

  const handleUpload = async () => {
    if (uploading()) return
    setUploading(true)
    try {
      const currentServer = server.current
      if (!currentServer || !("http" in currentServer) || !currentServer.http) {
        showToast({ variant: "error", title: "上传失败", description: "无法确定服务器地址" })
        return
      }
      const serverConn = { url: currentServer.http.url, username: currentServer.http.username, password: currentServer.http.password }
      const directory = sdk().directory

      // Default upload directory: parent of the currently selected file, or workspace root
      const activeFile = props.active
      const defaultDir = activeFile ? activeFile.split("/").slice(0, -1).join("/") : ""
      const startPath = defaultDir ? `${directory}/${defaultDir}` : directory

      dialog.show(() => (
        <DialogSelectDirectoryV2
          mode="directory"
          server={currentServer}
          start={startPath}
          title="选择上传目录"
          onSelect={(result) => {
            if (typeof result !== "string" || !result) return
            // result is an absolute path; compute the relative target directory
            const targetDir = result.startsWith(directory) ? result.slice(directory.length).replace(/^\/+/, "") : result
            const input = document.createElement("input")
            input.type = "file"
            input.onchange = async () => {
              const selected = input.files?.[0]
              if (!selected) return
              const targetPath = targetDir ? `${targetDir}/${selected.name}` : selected.name
              try {
                await uploadFile(serverConn, targetPath, selected, directory)
                file.tree.refresh(targetDir || "")
                showToast({ variant: "success", title: "上传成功", description: `${selected.name} → ${targetDir || "/"}` })
              } catch (err) {
                showToast({
                  variant: "error",
                  title: "上传失败",
                  description: err instanceof Error ? err.message : String(err),
                })
              }
            }
            input.click()
          }}
        />
      ))
    } finally {
      setUploading(false)
    }
  }

  // Keep the sidebar outside Kobalte Tabs.Content: a morphing content value
  // unmounts the whole panel on every file-tab switch and resets sidebar scroll.
  return (
    <SessionFilePanelV2
      toolbar={true}
      toolbarEnd={
        <>
          <TooltipV2 value="下载文件" placement="bottom">
            <IconButtonV2
              variant="ghost"
              size="small"
              disabled={!props.active}
              onClick={() => void handleDownload()}
              aria-label="下载文件"
              icon={<Icon name="download" size="small" />}
            />
          </TooltipV2>
          <TooltipV2 value="上传文件" placement="bottom">
            <IconButtonV2
              variant="ghost"
              size="small"
              disabled={uploading()}
              onClick={() => void handleUpload()}
              aria-label="上传文件"
              icon={<Icon name="cloud-upload" size="small" />}
            />
          </TooltipV2>
        </>
      }
      sidebar={
        <SessionReviewV2Sidebar
          open={sidebarOpened()}
          transition={props.state.sidebarTransition()}
          title={<span class="truncate">{title()}</span>}
          filter={filter()}
          onFilterChange={setFilter}
          onFilterKeyDown={onFilterKeyDown}
          filterAutofocus={props.placeholder}
          filterRef={props.filterRef}
          filterControls={resultsID}
          filterActiveDescendant={highlighted() ? optionID(highlighted()!) : undefined}
          filterExpanded={query().length > 0 && files().length > 0}
          width={props.state.sidebarWidth()}
          onWidthChange={props.state.resizeSidebar}
          onToggle={props.state.toggleSidebar}
        >
          <Show
            when={query()}
            fallback={
              <FileTreeV2
                active={props.active}
                kinds={props.kinds}
                onFileClick={(node) => props.onSelect(node.path)}
                onFileDoubleClick={(node) => props.onSelectPermanent(node.path)}
              />
            }
          >
            <Show
              when={!loading()}
              fallback={
                <div role="status" class="px-2 py-2 text-12-regular text-text-weak">
                  {language.t("common.loading")}
                  {language.t("common.loading.ellipsis")}
                </div>
              }
            >
              <Show
                when={files().length > 0}
                fallback={
                  <div role="status" class="px-2 py-2 text-12-regular text-text-weak">
                    {language.t("palette.empty")}
                  </div>
                }
              >
                <SessionFileListV2
                  id={resultsID}
                  role="listbox"
                  optionID={optionID}
                  files={files()}
                  kinds={props.kinds}
                  active={props.active}
                  highlighted={highlighted()}
                  onFileClick={(path) => {
                    setExplicitHighlight(path)
                    props.onSelect(path)
                  }}
                  onFileDoubleClick={props.onSelectPermanent}
                />
              </Show>
            </Show>
          </Show>
        </SessionReviewV2Sidebar>
      }
    >
      <Show
        when={!props.placeholder}
        fallback={
          <SessionFilePanelV2Empty>
            <div class="flex flex-col items-center gap-3 text-center text-text-weak">
              <Icon name="file-tree" size="large" />
              <div class="text-14-medium text-text-strong">{language.t("command.file.open")}</div>
              <div class="text-13-regular">{language.t("session.files.selectToOpen")}</div>
            </div>
          </SessionFilePanelV2Empty>
        }
      >
        <div class="min-h-0 flex-1">
          <Show when={props.tab} keyed>
            {(tab) => <SessionFileView tab={tab} />}
          </Show>
        </div>
      </Show>
    </SessionFilePanelV2>
  )
}
